import { Badge } from '@/components/ui/shadcn/badge'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import type { RouterOutput } from '@/config/trpc'
import { Link } from 'react-router'
import { AlertTriangle } from 'lucide-react'

type PostItem = RouterOutput['analytics']['overview']['posts'][number]

const platformIcons = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  tiktok: TikTokIcon,
  youtube: YoutubeIcon,
  facebook: FacebookIcon,
} as const

const phaseColors: Record<string, string> = {
  hot: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  warm: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  cool: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  archived: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
}

function formatCompact(n: number | null | undefined): string {
  if (n == null) return '-'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function MetricCell({ value, label }: { value: string; label: string }) {
  return (
    <div className="min-w-[52px] text-right">
      <p className="text-foreground text-sm font-medium tabular-nums">{value}</p>
      <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">{label}</p>
    </div>
  )
}

export function TrackedPostList({ posts, onImport }: { posts: PostItem[]; onImport?: () => void }) {
  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <button
          onClick={onImport}
          disabled={!onImport}
          className="bg-muted hover:bg-hover disabled:hover:bg-muted flex h-12 w-12 items-center justify-center rounded-full transition-colors disabled:cursor-default">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-muted-foreground/50">
            <path
              d="M3 10h14M10 3v14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div>
          <p className="text-foreground font-medium">No tracked posts yet</p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {onImport ? (
              <button onClick={onImport} className="text-primary hover:underline">
                Import posts
              </button>
            ) : (
              'Posts will appear here after you publish or import them.'
            )}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {posts.map(({ trackedPost, latestMetrics }) => {
        const PlatformIcon = platformIcons[trackedPost.platform as keyof typeof platformIcons]
        return (
          <Link
            key={trackedPost.id}
            to={`/analytics/${trackedPost.id}`}
            className="hover:bg-hover border-border/60 flex items-center gap-4 rounded-lg border-t px-3 py-3 transition-colors first:border-t-0">
            {/* Thumbnail */}
            <div className="bg-card border-border flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border">
              {trackedPost.mediaS3Url ? (
                <img src={trackedPost.mediaS3Url} alt="" className="h-full w-full object-cover" />
              ) : PlatformIcon ? (
                <PlatformIcon width={20} height={20} color="var(--foreground)" />
              ) : null}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="mb-0.5 flex items-center gap-1.5">
                {PlatformIcon && (
                  <PlatformIcon width={13} height={13} color="var(--muted-foreground)" />
                )}
                <span className="text-muted-foreground text-xs capitalize">
                  {trackedPost.platform}
                </span>
                {trackedPost.mediaType && (
                  <Badge variant="outline" className="border-border/60 px-1.5 py-0 text-[10px]">
                    {trackedPost.mediaType}
                  </Badge>
                )}
                <Badge
                  variant="secondary"
                  className={`px-1.5 py-0 text-[10px] ${phaseColors[trackedPost.trackingPhase] ?? ''}`}>
                  {trackedPost.trackingPhase}
                </Badge>
                {trackedPost.syncFailCount >= 3 && (
                  <Badge
                    variant="secondary"
                    className="bg-amber-100 px-1.5 py-0 text-[10px] text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <AlertTriangle size={10} className="mr-0.5" />
                    Sync failing
                  </Badge>
                )}
              </div>
              <p className="text-foreground truncate text-sm leading-snug">
                {trackedPost.caption || 'No caption'}
              </p>
              <p className="text-muted-foreground/60 mt-0.5 text-xs">
                {new Date(trackedPost.publishedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            {/* Metrics — compact on mobile, full on sm+ */}
            {latestMetrics && (
              <>
                <div className="text-right sm:hidden">
                  <p className="text-foreground text-sm font-medium tabular-nums">
                    {formatCompact(latestMetrics.views)}
                  </p>
                  <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">
                    Views
                  </p>
                </div>
                <div className="hidden items-center gap-5 sm:flex">
                  <MetricCell value={formatCompact(latestMetrics.views)} label="Views" />
                  <MetricCell value={formatCompact(latestMetrics.likes)} label="Likes" />
                  <MetricCell value={formatCompact(latestMetrics.comments)} label="Cmts" />
                  {latestMetrics.engagementRate != null && (
                    <MetricCell
                      value={`${(latestMetrics.engagementRate * 100).toFixed(1)}%`}
                      label="Eng."
                    />
                  )}
                </div>
              </>
            )}
          </Link>
        )
      })}
    </div>
  )
}
